import { Menu, Transition } from '@headlessui/react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { Typography } from '@mui/material';
import classNames from 'classnames';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { userService } from 'features/settings/users/api/user.service';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { IsTenantTechnicianCoordinatorOrAbove } from 'shared/permissions';
import {
  ROLE_SUPER_ADMIN,
  ROLE_TENANT_ADMIN,
  ROLE_TENANT_TECHNICIAN_COORDINATOR
} from 'shared/permissions/roles';
import { authActions } from 'store';
import { useAppDispatch, useAppSelector } from 'store/store';

export const WarehouseSelector = () => {
  const dispatch = useAppDispatch();

  const currentWarehouse = useAppSelector((state) => state.auth.warehouse);
  const groups = useAppSelector((state) => state.auth.groups);

  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const canChangeWarehouse = Boolean(
    groups &&
      (groups.includes(ROLE_TENANT_TECHNICIAN_COORDINATOR) ||
        groups.includes(ROLE_TENANT_ADMIN) ||
        groups.includes(ROLE_SUPER_ADMIN))
  );

  useEffect(() => {
    if (!canChangeWarehouse) {
      return;
    }
    warehouseService
      .getAllBaseInformation()
      .then((res) => {
        if (res && Array.isArray(res)) {
          setWarehouses(res.map((wh) => wh.code));
        } else {
          setWarehouses([]);
        }
      })
      .catch(() => {
        setWarehouses([]);
      });
  }, [canChangeWarehouse]);

  const filteredWarehouses = useMemo(() => {
    return warehouses.filter((wh) => wh.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [warehouses, searchQuery]);

  const handleSelectWarehouse = (value: string) => {
    if (currentWarehouse === value) return;

    userService.selectWarehouse(value).then((res) => {
      dispatch(authActions.selectWarehouse(res));
    });
  };

  const buttonLabel = currentWarehouse || (canChangeWarehouse ? 'Seleziona magazzino' : '');

  if (!canChangeWarehouse) {
    if (!currentWarehouse) {
      return null;
    }
    return (
      <div className="p-2 min-w-[200px] flex items-center gap-2">
        <WarehouseIcon color="primary" />
        <Typography variant="body2">{currentWarehouse}</Typography>
      </div>
    );
  }

  return (
    <IsTenantTechnicianCoordinatorOrAbove>
      <Menu
        as="div"
        className="relative inline-block text-left w-full max-w-[240px] font-sans select-none z-40">
        <div>
          {/* -------------------------------- BOTTONE PRINCIPALE -------------------------------- */}
          <Menu.Button className="flex items-center justify-between w-full bg-white border border-slate-300 hover:border-slate-400 rounded-md px-4 py-2 text-[14px] font-medium text-[#1c1f23] shadow-sm transition-all focus:outline-none">
            <span className="truncate">{buttonLabel}</span>
            <KeyboardArrowDownIcon
              sx={{ color: '#0d6efd', fontSize: '20px' }}
              className="ml-2 flex-shrink-0"
            />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95">
          {/* -------------------------------- PANNELLO POPUP PRINCIPALE -------------------------------- */}

          <Menu.Items className="absolute right-0 mt-1 w-[260px] origin-top-right rounded-lg bg-white shadow-xl border border-slate-200/80 focus:outline-none overflow-hidden p-2 flex flex-col gap-2">
            {/* -------------------------------- INPUT DI RICERCA INTERNO -------------------------------- */}

            <div
              className="relative flex items-center"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}>
              <input
                type="text"
                placeholder="Cerca"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-md pl-3 pr-9 py-1.5 text-[14px] text-slate-700 placeholder-slate-400 focus:outline-none focus:border-slate-400 font-normal"
              />
              <SearchIcon
                sx={{ color: '#868e96', fontSize: '18px' }}
                className="absolute right-3 pointer-events-none"
              />
            </div>

            {/* -------------------------------- LISTA DELLE OPZIONI -------------------------------- */}
            <div className="max-h-64 overflow-y-auto space-y-0.5 pr-0.5">
              {currentWarehouse && (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => handleSelectWarehouse('')}
                      className={classNames(
                        active ? 'bg-red-50' : 'bg-transparent',
                        'flex items-center w-full px-2 py-2 rounded-md cursor-pointer transition-colors text-left group focus:outline-none border-b border-slate-100 mb-1'
                      )}>
                      <span className="font-medium text-red-600 text-[14px] truncate">
                        Rimuovi selezione
                      </span>
                    </button>
                  )}
                </Menu.Item>
              )}

              {filteredWarehouses.length > 0 ? (
                filteredWarehouses.map((wh) => {
                  const isSelected = currentWarehouse === wh;

                  return (
                    <Menu.Item key={wh}>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={() => handleSelectWarehouse(wh)}
                          className={classNames(
                            active ? 'bg-slate-50' : 'bg-transparent',
                            'flex items-center w-full px-2 py-2 rounded-md cursor-pointer transition-colors text-left group focus:outline-none'
                          )}>
                          <DragIndicatorIcon
                            sx={{ color: '#adb5bd', fontSize: '18px' }}
                            className="mr-2 flex-shrink-0"
                          />

                          <div className="mr-3 flex items-center shrink-0">
                            {isSelected ? (
                              <CheckBoxIcon sx={{ color: '#0d6efd', fontSize: '20px' }} />
                            ) : (
                              <CheckBoxOutlineBlankIcon
                                sx={{ color: '#adb5bd', fontSize: '20px' }}
                              />
                            )}
                          </div>

                          <span
                            className={classNames(
                              isSelected
                                ? 'font-semibold text-[#0d6efd]'
                                : 'font-medium text-[#1c1f23]',
                              'text-[14px] truncate'
                            )}>
                            {wh}
                          </span>
                        </button>
                      )}
                    </Menu.Item>
                  );
                })
              ) : (
                <div className="px-4 py-3 text-center text-[12px] text-slate-400">
                  Nessun magazzino trovato
                </div>
              )}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </IsTenantTechnicianCoordinatorOrAbove>
  );
};

export default WarehouseSelector;
